import React, { useState, useEffect } from 'react';
import moment from 'moment';
import WindowQuery from '../utils/WindowQuery';
import storeQueryInSession from '../utils/storeQueryInSession';

export const ListingMapContext = React.createContext();

export default function MapProvider({ children, categories }) {
  const updateQuery = new WindowQuery();

  const [datesVisible, setDatesVisible] = useState(false);
  const [guestsVisible, setGuestsVisible] = useState(false);
  const [priceVisible, setPriceVisible] = useState(false);
  const [sortByVisible, setSortByVisible] = useState(false);
  const [categoriesVisible, setCategoriesVisible] = useState(false);
  const [typesVisible, setTypesVisible] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);

  const [searchValue, setSearchValue] = useState('');

  const [city, setCity] = useState(null);
  const [cityId, setCityId] = useState(null);
  const [showFilters, toggleFilters] = useState(false);
  const [mapCategories, setMapCategories] = useState([]);
  const [mapStartDate, setStartDate] = useState(null);
  const [mapEndDate, setEndDate] = useState(null);
  const [guests, setGuests] = useState(0);
  const [minPrice, setMinPrice] = useState();
  const [maxPrice, setMaxPrice] = useState();
  const [bedrooms, setBedrooms] = useState(0);
  const [bathrooms, setBathrooms] = useState(0);
  const [propertyType, setPropertyType] = useState('');
  const [rentalSearchAmenities, setRentalSearchAmenities] = useState([]);
  const [communityId, setCommunityId] = useState();
  const [titleCont, setTitleCont] = useState('');
  const [rentalSortBy, setRentalSortBy] = useState();
  const [listingSortBy, setListingSortBy] = useState();
  const [isTypeIn, setIsTypeIn] = useState([]);
  const [minScore, setMinScore] = useState();
  const [maxScore, setMaxScore] = useState();
  const [isPremier, setIsPremier] = useState();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [searchOnMove, setSearchOnMove] = useState(true);
  const [showSearchOnMove, setShowSearchOnMove] = useState(false);
  const [disableQueryConstraints, setDisableQueryConstraints] = useState(false);
  const [showDeals, setShowDeals] = useState(false);

  const [meta, setMeta] = useState(null);
  const [zoom, setZoom] = useState(10);
  const [mapCenter, setMapCenter] = useState({
    lat: 30.392740999999987,
    lng: -86.45752999999999,
  });

  useEffect(() => {
    const mapType = window.location.search
      ? window.location.search.split('&')[0].split('=')[1]
      : 'rentals';

    const storedQuery = window.sessionStorage.getItem(`${mapType}_map_query`);
    const currentQuery = window.location.search;

    const storedQuerySplit = storedQuery
      ? storedQuery.substr(1).split('&')
      : [];
    const currentQuerySplit = currentQuery.substr(1).split('&');

    // Prevents both comm_id and area filter both being active
    if (storedQuery && storedQuery !== currentQuery) {
      // Remove community filter from stored query
      if (
        storedQuery.includes('&comm_id=') &&
        currentQuery.includes('&area=')
      ) {
        // Remove community filter from stored query
        const commQueryIndex = storedQuerySplit.findIndex(q =>
          q.includes('comm_id=')
        );
        storedQuerySplit.splice(commQueryIndex, 1);
      } else if (
        storedQuery.includes('&area=') &&
        currentQuery.includes('&comm_id=')
      ) {
        // Remove area filter from stored query
        const areaQueryIndex = storedQuerySplit.findIndex(q =>
          q.includes('area=')
        );
        storedQuerySplit.splice(areaQueryIndex, 1);
      }

      const usedIndexes = [];
      currentQuerySplit.forEach(query => {
        const oldQueryIndex = storedQuerySplit.findIndex(q => {
          const [queryName] = query.split('=');
          const [qName] = q.split('=');
          if (queryName === 'is_type_in' && qName === 'type') {
            return false;
          }
          return query.includes(q.split('=')[0]);
        });

        // If stored query not found
        if (oldQueryIndex === -1) {
          storedQuerySplit.push(query);
        } else if (!usedIndexes.includes(oldQueryIndex)) {
          storedQuerySplit.splice(oldQueryIndex, 1, query);
          usedIndexes.push(oldQueryIndex);
        }
      });

      window.sessionStorage.setItem(
        `${mapType}_map_query`,
        `?${storedQuerySplit.join('&')}`
      );

      let sameQueries = true;

      window.location.search
        .substr(1)
        .split('&')
        .forEach(q => {
          if (!storedQuerySplit.includes(q)) sameQueries = false;
        });

      if (!sameQueries) {
        window.location.search = `?${storedQuerySplit.join('&')}`;
      }
    }
  }, []);

  // Set queries in session storage when query changes
  useEffect(() => {
    storeQueryInSession('map_type', 'rentals', 'rentals_map_query');
    storeQueryInSession('map_type', 'listings', 'listings_map_query');

    const mapAreaQuery = window.location.search
      .split('&')
      .find(q => q.includes('area'));
    const mapArea = mapAreaQuery ? mapAreaQuery.split('=')[1] : '';

    const [mapType] = window.location.search
      ? window.location.search
          .split('&')
          .find(q => q.includes('map_type'))
          .split('=')
      : ['rentals'];

    if (mapArea) {
      storeQueryInSession('area', mapArea, 'rentals_map_query');
      storeQueryInSession('area', mapArea, 'listings_map_query');
    }

    if (mapStartDate)
      storeQueryInSession(
        'check_in',
        moment(mapStartDate).format('YYYYMMDD'),
        'rentals_map_query'
      );
    if (mapEndDate)
      storeQueryInSession(
        'check_out',
        moment(mapEndDate).format('YYYYMMDD'),
        'rentals_map_query'
      );
    if (guests) storeQueryInSession('guests', guests, 'rentals_map_query');
    if (minPrice)
      storeQueryInSession('min_price', minPrice, 'rentals_map_query');
    if (maxPrice)
      storeQueryInSession('max_price', maxPrice, 'rentals_map_query');
    if (propertyType)
      storeQueryInSession('property_type', propertyType, 'rentals_map_query');
    if (rentalSearchAmenities.length)
      storeQueryInSession(
        'amenities',
        rentalSearchAmenities.join(','),
        'rentals_map_query'
      );
    if (bedrooms) storeQueryInSession('beds', bedrooms, 'rentals_map_query');
    if (bathrooms) storeQueryInSession('baths', bathrooms, 'rentals_map_query');
    if (isPremier) {
      storeQueryInSession('is_premier', isPremier, 'rentals_map_query');
      storeQueryInSession('is_premier', isPremier, 'listings_map_query');
    }
    if (isTypeIn.length)
      storeQueryInSession(
        'is_type_in',
        isTypeIn.join(','),
        'listings_map_query'
      );
    if (mapCategories.length)
      storeQueryInSession(
        'categories',
        mapCategories.join(','),
        'listings_map_query'
      );
    if (rentalSortBy && mapType === 'rentals') {
      storeQueryInSession(
        'rentals_sort_by',
        rentalSortBy.value,
        'rentals_map_query'
      );
    }
    if (listingSortBy && mapType === 'listings') {
      storeQueryInSession(
        'listings_sort_by',
        listingSortBy.value,
        'listings_map_query'
      );
    }
  }, [
    mapStartDate,
    mapEndDate,
    guests,
    minPrice,
    maxPrice,
    propertyType,
    bedrooms,
    bathrooms,
    rentalSearchAmenities,
    isPremier,
    isTypeIn,
    mapCategories,
    rentalSortBy,
    listingSortBy,
  ]);

  const clearAllFilters = mapType => {
    setIsPremier(null);
    updateQuery.remove('is_premier');
    updateQuery.remove('sort_by');
    window.sessionStorage.removeItem(`${mapType}_map_query`);

    if (mapType === 'rentals') {
      setRentalSortBy(null);
      setStartDate(null);
      setEndDate(null);
      setGuests(0);
      setMinPrice(null);
      setMaxPrice(null);
      setMaxPrice(null);
      setPropertyType('');
      setBedrooms(0);
      setBathrooms(0);
      setRentalSearchAmenities([]);
      updateQuery.remove('check_in');
      updateQuery.remove('check_out');
      updateQuery.remove('guests');
      updateQuery.remove('min_price');
      updateQuery.remove('max_price');
      updateQuery.remove('beds');
      updateQuery.remove('baths');
      updateQuery.remove('property_type');
      updateQuery.remove('amenities');
    } else {
      // Clear Listing Filters
      setRentalSortBy(null);
      setIsTypeIn([]);
      setMapCategories([]);
      updateQuery.remove('is_type');
      updateQuery.remove('is_type_in');
      updateQuery.remove('categories');
    }
  };

  const setDropdown = dropdown => {
    switch (dropdown) {
      case 'dates':
        setDatesVisible(true);
        setGuestsVisible(false);
        setPriceVisible(false);
        setSortByVisible(false);
        break;

      case 'guests':
        setDatesVisible(false);
        setGuestsVisible(true);
        setPriceVisible(false);
        setSortByVisible(false);
        break;

      case 'price':
        setDatesVisible(false);
        setGuestsVisible(false);
        setPriceVisible(true);
        setSortByVisible(false);
        break;

      case 'sort_by':
        setDatesVisible(false);
        setGuestsVisible(false);
        setPriceVisible(false);
        setCategoriesVisible(false);
        setTypesVisible(false);
        setSortByVisible(true);
        break;

      case 'categories':
        setCategoriesVisible(true);
        setSortByVisible(false);
        setTypesVisible(false);
        break;

      case 'types':
        setTypesVisible(true);
        setSortByVisible(false);
        setCategoriesVisible(false);
        break;

      case null:
        setDatesVisible(false);
        setGuestsVisible(false);
        setPriceVisible(false);
        setCategoriesVisible(false);
        setSortByVisible(false);
        setTypesVisible(false);
        break;

      default:
        console.error(
          `%c No Dropdown case for ${dropdown} found!`,
          'font-size: 28px;'
        );
        break;
    }
  };

  return (
    <ListingMapContext.Provider
      value={{
        setDropdown,
        datesVisible,
        guestsVisible,
        priceVisible,
        sortByVisible,
        categoriesVisible,
        setDatesVisible,
        setGuestsVisible,
        setPriceVisible,
        setSortByVisible,
        setCategoriesVisible,
        categories,
        mapCategories,
        setMapCategories,
        showFilters,
        mapStartDate,
        mapEndDate,
        guests,
        minPrice,
        maxPrice,
        bedrooms,
        bathrooms,
        propertyType,
        rentalSearchAmenities,
        toggleFilters,
        setStartDate,
        setEndDate,
        setGuests,
        setMinPrice,
        setMaxPrice,
        setBedrooms,
        setBathrooms,
        setPropertyType,
        setRentalSearchAmenities,
        showFavorites,
        setShowFavorites,
        searchValue,
        setSearchValue,
        communityId,
        setCommunityId,
        titleCont,
        setTitleCont,
        mapCenter,
        setMapCenter,
        typesVisible,
        isTypeIn,
        setIsTypeIn,
        minScore,
        setMinScore,
        maxScore,
        setMaxScore,
        isPremier,
        setIsPremier,
        hoveredItem,
        setHoveredItem,
        zoom,
        setZoom,
        meta,
        setMeta,
        searchOnMove,
        setSearchOnMove,
        showSearchOnMove,
        setShowSearchOnMove,
        disableQueryConstraints,
        setDisableQueryConstraints,
        clearAllFilters,
        city,
        setCity,
        cityId,
        setCityId,
        rentalSortBy,
        listingSortBy,
        setRentalSortBy,
        setListingSortBy,
        showDeals,
        setShowDeals,
      }}
    >
      {children}
    </ListingMapContext.Provider>
  );
}
